import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { forkJoin, Observable, of, ReplaySubject, Subject } from "rxjs";
import { filter, switchMap, take, takeUntil } from "rxjs/operators";

import { BlockingUiSignalService } from "../blocking-ui-signal.service";
import { CoachMarkService } from "../coach-mark.service";
import { InteractionTrackingService, InteractionType } from "../interaction-tracking.service";
import { OnboardingStep, OnboardingStepService } from "../onboarding-step.service";
import { PaymentsService } from "../payments.service";
import { FO01_LESSON_ID } from "./fo01-flexible-order.service";
import { isOnRoute, whenUiQuiet } from "./onboarding-lesson-modal";
import { RS01_LESSON_ID, RS02_LESSON_ID } from "./rs-repeat-scoring.service";
import { S01B_LESSON_ID } from "./s01b-start-another-score.service";
import { SCORE_LANDING_ROUTE } from "./s01-score-introduction.service";
import { ScoreContextualEntryService } from "./score-contextual-entry.service";

export const SS01_LESSON_ID = "SS01";
export const SS01B_LESSON_ID = "SS01B";

/** Recorded with SS01's completed lesson state - its CompletedAt *is* this milestone. */
export const SCORE_SUPER_STARS_INTRODUCED = "score_super_stars_introduced";

/** Recorded with SS01B's completed lesson state (workbook v2.47 Milestones sheet). */
export const SCORE_SUPER_STARS_VIEW_ALL_INTRODUCED = "score_super_stars_view_all_introduced";

/** Anchors workbook v2.38: the Super Stars area on the Score landing page. */
export const SS01_ANCHOR = "score.super_stars";
/** Anchors workbook v2.47: the View All control near the Super Stars area. */
export const SS01B_ANCHOR = "score.super_stars_view_all";

/** Copy rows SS01.01-SS01.02, workbook v2.38 Copy sheet (verbatim). */
export const SS01_COPY = {
  heading: "Meet your Super Stars",
  body: "Super Stars are the Stars that have contributed the most points across your scoring history.",
};

/** Copy rows SS01B.01-SS01B.02, workbook v2.47 Copy sheet (verbatim). */
export const SS01B_COPY = {
  heading: "Add a Star to your Score",
  body: "Click View All to see all of your Super Stars, and add one to a new scoring session.",
};

/**
 * Onboarding lesson SS01 "Meet your Super Stars" (FLIP-371; spec v2.36 §18.4, workbook v2.38).
 * Informational anchored prompt (no buttons) at the Super Stars section of the Score landing page.
 *
 * - Eligible while the `onboarding-walkthrough` flag is on and SS01 is unresolved, on a landing
 *   visit after one submitted Score, once at least one ranked Star is showing (super_stars_available)
 *   and the UI is quiet. Any failed check holds back.
 * - Last in the landing queue, and never in an entry where RS01, RS02, S01B or FO01 appeared.
 * - Opening Super Stars (View all) while the prompt shows, or the visible close (standard
 *   informational dismissal), completes SS01 permanently. Leaving the page closes the prompt and
 *   records nothing.
 * - SS02 (FLIP-372) follows inside the Super Stars modal once SS01 is resolved (hasResolved) and
 *   SS01B has had its turn (hasPendingViewAll).
 *
 * SS01B "Add a Star to your Score" (workbook v2.47) is SS01's sibling, anchored at the View All
 * control: it fires immediately when SS01 is resolved by the standard informational dismissal, same
 * visit - and not when SS01 resolved by opening Super Stars directly. Informational: its dismissal,
 * or opening Super Stars via View All, completes it. Leaving the page closes it and records nothing;
 * it is not offered on a later visit (its trigger is SS01's dismissal), and SS02 stops waiting on it.
 */
@Injectable({ providedIn: "root" })
export class SS01SuperStarsService {
  private readonly cancel$ = new Subject<void>();
  private outcome$: ReplaySubject<boolean> | null = null;
  private promptOpen = false;
  /** SS01B is waiting to show or showing (the user dismissed SS01 and has not yet moved on). */
  private viewAllPending = false;
  private viewAllOpen = false;
  /** Completed in this app session - its write may still be in flight. */
  private completed = false;

  constructor(
    private router: Router,
    private coachMarkService: CoachMarkService,
    private blockingUiSignal: BlockingUiSignalService,
    private paymentsService: PaymentsService,
    private onboardingStepService: OnboardingStepService,
    private entry: ScoreContextualEntryService,
    private interactionTracking: InteractionTrackingService
  ) {
    // Logout emits false - forget this account's state.
    this.paymentsService.subscriptionActive$.pipe(filter((active) => !active)).subscribe(() => {
      this.completed = false;
      this.viewAllPending = false;
      this.leave();
    });
  }

  /** Whether SS01 was completed in this app session - SS02 needn't wait for the write. */
  get hasResolved(): boolean {
    return this.completed;
  }

  /** SS01B is waiting to show or showing - SS02 holds back until it has resolved or been left. */
  get hasPendingViewAll(): boolean {
    return this.viewAllPending;
  }

  /**
   * Call on arrival at the Score landing page, once RS02 and S01B have passed. `hasRankedStars$`
   * emits whether the Super Stars section has at least one Star. Emits once - true if the prompt
   * was presented, false if not. Never emits if the user leaves first.
   */
  maybeShow(hasRankedStars$: Observable<boolean>): Observable<boolean> {
    this.cancelPending();
    const outcome$ = new ReplaySubject<boolean>(1);
    this.outcome$ = outcome$;

    this.entry
      .enabled$()
      .pipe(
        switchMap(() =>
          forkJoin([
            this.onboardingStepService.getStep(SS01_LESSON_ID),
            this.onboardingStepService.getSubmittedScoreCount(),
            hasRankedStars$.pipe(take(1)),
          ])
        ),
        switchMap(([step, scoreCount, hasStars]) => {
          if (!this.isEligible(step, scoreCount, hasStars)) {
            return of(false as const);
          }
          return whenUiQuiet(this.blockingUiSignal.isBlockingUiActive$).pipe(
            switchMap(() => of(this.canShowNow() ? (step ?? null) : (false as const)))
          );
        }),
        take(1),
        takeUntil(this.cancel$)
      )
      .subscribe({
        next: (step) => {
          if (step !== false) {
            this.open(step);
          }
          this.settle(step !== false);
        },
        // The flag being off completes without a value.
        complete: () => this.settle(false),
      });

    return outcome$.pipe(take(1));
  }

  /**
   * Super Stars opened (View all): completes SS01 while its prompt shows - directly, so SS01B is
   * skipped - or SS01B while that shows.
   */
  onSuperStarsOpened(): void {
    if (this.viewAllOpen) {
      this.viewAllOpen = false;
      this.viewAllPending = false;
      this.coachMarkService.dismiss();
      this.track("onboarding_lesson_action", "action: super_stars_opened", SS01B_LESSON_ID);
      this.completeViewAll("opened_super_stars");
      return;
    }
    if (!this.promptOpen) return;
    this.promptOpen = false;
    this.coachMarkService.dismiss();
    this.track("onboarding_lesson_action", "action: super_stars_opened");
    this.complete();
  }

  /** Call when leaving the Score landing page. Records nothing. */
  leave(): void {
    this.cancelPending();
    this.viewAllPending = false;
    if (this.promptOpen || this.viewAllOpen) {
      this.promptOpen = false;
      this.viewAllOpen = false;
      this.coachMarkService.dismiss();
    }
  }

  private cancelPending(): void {
    const outcome$ = this.outcome$;
    this.outcome$ = null;
    outcome$?.complete();
    this.cancel$.next();
  }

  private isEligible(step: OnboardingStep | null | undefined, scoreCount: number | null, hasStars: boolean): boolean {
    // Only definite answers qualify - "couldn't check" holds back rather than guessing.
    return (
      !this.completed &&
      step !== undefined &&
      !step?.completedAt &&
      !step?.dismissed &&
      scoreCount !== null &&
      scoreCount >= 1 &&
      hasStars
    );
  }

  private canShowNow(): boolean {
    return (
      !this.completed &&
      isOnRoute(this.router, SCORE_LANDING_ROUTE) &&
      !this.entry.presentedThisEntry(RS01_LESSON_ID, RS02_LESSON_ID, S01B_LESSON_ID, FO01_LESSON_ID)
    );
  }

  private open(step: OnboardingStep | null): void {
    this.promptOpen = true;
    this.entry.markPresented(SS01_LESSON_ID);
    const now = new Date();
    this.onboardingStepService.upsertStep({
      stepKey: SS01_LESSON_ID,
      lastViewedAt: now,
      ...(step?.startedAt ? {} : { startedAt: now }),
    });

    // The engine tracks started / presented and the close for lessonId.
    this.coachMarkService.show({
      target: SS01_ANCHOR,
      heading: SS01_COPY.heading,
      message: SS01_COPY.body,
      lessonId: SS01_LESSON_ID,
      // Informational: the standard dismissal completes SS01.
      onDismiss: () => {
        this.promptOpen = false;
        this.complete();
        // SS01B follows immediately - after the engine has finished tearing this prompt down.
        this.viewAllPending = true;
        Promise.resolve().then(() => this.openViewAll());
      },
    });
  }

  /** SS01B, at the View All control - only while the user is still on the Score landing page. */
  private openViewAll(): void {
    if (!isOnRoute(this.router, SCORE_LANDING_ROUTE) || this.coachMarkService.isShowing) {
      this.viewAllPending = false;
      return;
    }
    this.viewAllOpen = true;
    const now = new Date();
    this.onboardingStepService.upsertStep({ stepKey: SS01B_LESSON_ID, lastViewedAt: now, startedAt: now });
    this.coachMarkService.show({
      target: SS01B_ANCHOR,
      heading: SS01B_COPY.heading,
      message: SS01B_COPY.body,
      lessonId: SS01B_LESSON_ID,
      // Informational: the standard dismissal completes SS01B.
      onDismiss: () => {
        this.viewAllOpen = false;
        this.viewAllPending = false;
        this.completeViewAll("dismissed");
      },
    });
  }

  private completeViewAll(reason: string): void {
    this.onboardingStepService.upsertStep({ stepKey: SS01B_LESSON_ID, completedAt: new Date() });
    this.track(
      "onboarding_lesson_state_changed",
      `completed; milestone: ${SCORE_SUPER_STARS_VIEW_ALL_INTRODUCED}; reason: ${reason}`,
      SS01B_LESSON_ID
    );
  }

  private complete(): void {
    this.completed = true;
    this.onboardingStepService.upsertStep({ stepKey: SS01_LESSON_ID, completedAt: new Date() });
    this.track("onboarding_lesson_state_changed", `completed; milestone: ${SCORE_SUPER_STARS_INTRODUCED}`);
  }

  private settle(presented: boolean): void {
    this.outcome$?.next(presented);
    this.outcome$?.complete();
    this.outcome$ = null;
  }

  /** Same shape as CoachMarkService's FLIP-322 lesson events. */
  private track(eventName: string, detail?: string, lessonId: string = SS01_LESSON_ID): void {
    this.interactionTracking.track({
      screen: this.router.url,
      eventName,
      interactionType: InteractionType.Selection,
      entityType: "OnboardingLesson",
      detail: detail ? `lesson: ${lessonId}; ${detail}` : `lesson: ${lessonId}`,
    });
  }
}
